import React, { useRef, useState } from 'react';
import { Button, Grid, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// @ts-ignore
import useKeypress from 'react-use-keypress';
import { currencyFormatter } from '@utils/Numbers';

interface CalculatorProps {
    disabled?: boolean;
    showAmount?: boolean;
    disableKeyPress?: boolean;
    /** Importe con el que arranca (editar un gasto). Solo cuenta al montar: se resetea remontando. */
    initialValue?: number;
    onChangeValue: (value: number) => void;
}

/**
 * ¿La tecla venía de un campo de texto? `useKeypress` escucha en `window`, así
 * que SIN este guard la calculadora se comía TODO el teclado de la pantalla:
 * capturar el PIN del corte de caja escribía el número en la calculadora, y
 * teclear la descripción de un gasto metía los dígitos al importe (y el
 * Backspace de corregir la descripción borraba el importe).
 */
const isTypingInField = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return false;
    if (target.isContentEditable) return true;
    return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
};

const Calculator: React.FunctionComponent<CalculatorProps> = (props) => {
    const { disabled, showAmount = true, disableKeyPress, initialValue, onChangeValue } = props;
    const [amount, setAmount] = useState<number>(initialValue || 0);
    const rootRef = useRef<HTMLDivElement>(null);

    /**
     * ¿La tecla venía de OTRA capa? Si hay un modal abierto encima (p. ej. el
     * cobro en efectivo sobre la pantalla de gastos, que tiene su propia
     * calculadora), las dos calculadoras están montadas y las dos escuchan
     * `window`: la misma tecla actualizaba ambos importes. La regla: solo
     * proceso la tecla si el foco vive en MI misma capa de diálogo (o ninguna).
     */
    const isFromAnotherLayer = (target: EventTarget | null): boolean => {
        if (!(target instanceof HTMLElement)) return false;
        const targetDialog = target.closest('.MuiDialog-root');
        const ownDialog = rootRef.current?.closest('.MuiDialog-root') ?? null;
        return targetDialog !== ownDialog;
    };

    const onPressNumber = (value: number) => {
        const newValue = Number((amount * 100).toFixed(0) + value.toString()) / 100;
        setAmount(newValue);
        onChangeValue(newValue);
    };

    const onRemove = () => {
        // Inverso EXACTO de onPressNumber (modo centavos): quitar el último
        // dígito capturado. El código viejo recortaba el string decimal, que no
        // es el inverso: de $5.00 un Backspace saltaba a $0 (toString es "5") y
        // de $12.34 daba $12.30 en vez de $1.23.
        const cents = Number((amount * 100).toFixed(0));
        const newValue = Math.floor(cents / 10) / 100;
        setAmount(newValue);
        onChangeValue(newValue);
    };

    useKeypress(['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'Backspace'], (event: KeyboardEvent) => {
        // `disabled` también aplica al teclado físico: los botones en pantalla se
        // deshabilitan, pero el listener global seguía escribiendo.
        if (disabled || disableKeyPress) return;
        if (isTypingInField(event.target)) return;
        if (isFromAnotherLayer(event.target)) return;
        if (event.key === 'Backspace') {
            onRemove();
        } else {
            onPressNumber(Number(event.key));
        }
    });

    return (
        <Stack ref={rootRef} alignItems="center" spacing={1}>
            {showAmount && (
                <Typography variant="h3" fontWeight={600}>
                    {currencyFormatter(amount)}
                </Typography>
            )}
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <Button
                        disabled={disabled}
                        fullWidth
                        variant="outlined"
                        onClick={() => onPressNumber(1)}
                        sx={{ borderColor: 'lightgray', color: 'black', fontWeight: 600, fontSize: 25 }}
                    >
                        1
                    </Button>
                </Grid>
                <Grid item xs={4}>
                    <Button
                        disabled={disabled}
                        fullWidth
                        variant="outlined"
                        onClick={() => onPressNumber(2)}
                        sx={{ borderColor: 'lightgray', color: 'black', fontWeight: 600, fontSize: 25 }}
                    >
                        2
                    </Button>
                </Grid>
                <Grid item xs={4}>
                    <Button
                        disabled={disabled}
                        fullWidth
                        variant="outlined"
                        onClick={() => onPressNumber(3)}
                        sx={{ borderColor: 'lightgray', color: 'black', fontWeight: 600, fontSize: 25 }}
                    >
                        3
                    </Button>
                </Grid>
                <Grid item xs={4}>
                    <Button
                        disabled={disabled}
                        fullWidth
                        variant="outlined"
                        onClick={() => onPressNumber(4)}
                        sx={{ borderColor: 'lightgray', color: 'black', fontWeight: 600, fontSize: 25 }}
                    >
                        4
                    </Button>
                </Grid>
                <Grid item xs={4}>
                    <Button
                        disabled={disabled}
                        fullWidth
                        variant="outlined"
                        onClick={() => onPressNumber(5)}
                        sx={{ borderColor: 'lightgray', color: 'black', fontWeight: 600, fontSize: 25 }}
                    >
                        5
                    </Button>
                </Grid>
                <Grid item xs={4}>
                    <Button
                        disabled={disabled}
                        fullWidth
                        variant="outlined"
                        onClick={() => onPressNumber(6)}
                        sx={{ borderColor: 'lightgray', color: 'black', fontWeight: 600, fontSize: 25 }}
                    >
                        6
                    </Button>
                </Grid>
                <Grid item xs={4}>
                    <Button
                        disabled={disabled}
                        fullWidth
                        variant="outlined"
                        onClick={() => onPressNumber(7)}
                        sx={{ borderColor: 'lightgray', color: 'black', fontWeight: 600, fontSize: 25 }}
                    >
                        7
                    </Button>
                </Grid>
                <Grid item xs={4}>
                    <Button
                        disabled={disabled}
                        fullWidth
                        variant="outlined"
                        onClick={() => onPressNumber(8)}
                        sx={{ borderColor: 'lightgray', color: 'black', fontWeight: 600, fontSize: 25 }}
                    >
                        8
                    </Button>
                </Grid>
                <Grid item xs={4}>
                    <Button
                        disabled={disabled}
                        fullWidth
                        variant="outlined"
                        onClick={() => onPressNumber(9)}
                        sx={{ borderColor: 'lightgray', color: 'black', fontWeight: 600, fontSize: 25 }}
                    >
                        9
                    </Button>
                </Grid>
                <Grid item xs={4} />
                <Grid item xs={4}>
                    <Button
                        disabled={disabled}
                        fullWidth
                        variant="outlined"
                        onClick={() => onPressNumber(0)}
                        sx={{ borderColor: 'lightgray', color: 'black', fontWeight: 600, fontSize: 25 }}
                    >
                        0
                    </Button>
                </Grid>
                <Grid item xs={4}>
                    <Button
                        disabled={disabled}
                        fullWidth
                        variant="outlined"
                        onClick={onRemove}
                        sx={{ height: '100%', borderColor: 'lightgray' }}
                    >
                        <ArrowBackIcon color="error" />
                    </Button>
                </Grid>
            </Grid>
        </Stack>
    );
};

export default Calculator;
